import {
  Button,
  Checkbox,
  FormDialog,
  FormError,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../api";
import { copyTableEntries, tableNameKey } from "./copyTableEntries";
import type { TableConfig } from "./types";

interface TableSource {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  tableCount: number;
}

/** A source table plus whether the target already has one by that name. */
interface Candidate {
  name: string;
  alreadyConfigured: boolean;
}

/** State that belongs to one chosen source, so switching source discards it. */
interface PerSource<T> {
  sourceId: string;
  value: T;
}

function toCandidates(sourceTables: Record<string, unknown>[], taken: Set<string>): Candidate[] {
  return sourceTables
    .map((t) => (typeof t.name === "string" ? t.name : ""))
    .filter((name) => name.trim() !== "")
    .map((name) => ({ name, alreadyConfigured: taken.has(tableNameKey(name)) }));
}

/** Same-type, non-archived peers of this connector that have tables to copy. */
function useTableSources(open: boolean, connectorId: string) {
  const [sources, setSources] = useState<TableSource[] | null>(null);
  const [sourcesError, setSourcesError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiFetch<TableSource[]>(`/api/connectors/${connectorId}/table-sources`)
      .then((s) => {
        if (!cancelled) setSources(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setSourcesError(e instanceof Error ? e.message : "Failed to load sources");
      });
    return () => {
      cancelled = true;
    };
  }, [open, connectorId]);

  return { sources, sourcesError };
}

/**
 * The chosen source's `vars.tables`. Both the result and the error are tagged
 * with the source they came from and read back through that tag, so switching
 * source discards them without a reset effect.
 */
function useSourceTables(sourceId: string) {
  const [loaded, setLoaded] = useState<PerSource<Record<string, unknown>[]> | null>(null);
  const [failed, setFailed] = useState<PerSource<string> | null>(null);

  useEffect(() => {
    if (!sourceId) return;
    let cancelled = false;
    apiFetch<Record<string, unknown>[]>(`/api/connectors/${sourceId}/tables`)
      .then((value) => {
        if (!cancelled) setLoaded({ sourceId, value });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setFailed({ sourceId, value: e instanceof Error ? e.message : "Failed to load tables" });
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  return {
    tables: loaded?.sourceId === sourceId ? loaded.value : null,
    tablesError: failed?.sourceId === sourceId ? failed.value : null,
  };
}

function CandidateList({
  candidates,
  selected,
  onToggle,
}: {
  candidates: Candidate[];
  selected: Set<string>;
  onToggle: (name: string) => void;
}) {
  return (
    <fieldset className="max-h-64 overflow-y-auto rounded-md border border-border">
      <legend className="sr-only">Tables to copy</legend>
      {candidates.map((c) => {
        const id = `copy-table-${tableNameKey(c.name)}`;
        return (
          <div key={c.name} className="flex items-center gap-2 border-b border-border px-3 py-1.5">
            <Checkbox
              id={id}
              checked={selected.has(tableNameKey(c.name))}
              disabled={c.alreadyConfigured}
              onCheckedChange={() => onToggle(c.name)}
            />
            <Label
              htmlFor={id}
              className={c.alreadyConfigured ? "text-muted-foreground" : "cursor-pointer"}
            >
              {c.name}
            </Label>
            {c.alreadyConfigured && (
              <span className="ml-auto text-xs text-muted-foreground">already configured</span>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}

function SourceSelect({
  sources,
  sourceId,
  onChange,
}: {
  sources: TableSource[] | null;
  sourceId: string;
  onChange: (id: string) => void;
}) {
  return (
    <div>
      <Label htmlFor="copy-source" className="form-label">
        Copy from
      </Label>
      <Select value={sourceId} onValueChange={onChange}>
        <SelectTrigger id="copy-source">
          <SelectValue placeholder="Select a connector…" />
        </SelectTrigger>
        <SelectContent>
          {(sources ?? []).map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.projectName} — {s.name} ({s.tableCount} tables)
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SelectionToolbar({
  selectedCount,
  selectableCount,
  onSelectAll,
  onClear,
}: {
  selectedCount: number;
  selectableCount: number;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">
        {selectedCount} of {selectableCount} selected
      </span>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onSelectAll}>
          Select all
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

interface AddTablesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The connector being edited. Sources are its same-type, non-archived peers. */
  connectorId: string;
  existingTables: TableConfig[];
  onAdd: (tables: TableConfig[]) => void;
}

export function AddTablesDialog({
  open,
  onOpenChange,
  connectorId,
  existingTables,
  onAdd,
}: AddTablesDialogProps) {
  const [sourceId, setSourceId] = useState("");
  const [touched, setTouched] = useState<PerSource<Set<string>> | null>(null);
  const { sources, sourcesError } = useTableSources(open, connectorId);
  const { tables, tablesError } = useSourceTables(sourceId);

  const taken = useMemo(
    () => new Set(existingTables.map((t) => tableNameKey(t.name))),
    [existingTables],
  );
  const candidates = useMemo(() => (tables ? toCandidates(tables, taken) : []), [tables, taken]);
  const selectable = candidates.filter((c) => !c.alreadyConfigured);

  // Untouched means "everything copyable" — the common case is taking the whole
  // connector, and unchecking a few beats checking seventy. Derived rather than
  // seeded into state so switching source needs no reset.
  const defaultSelection = useMemo(
    () => new Set(selectable.map((c) => tableNameKey(c.name))),
    [selectable],
  );
  const selected = touched?.sourceId === sourceId ? touched.value : defaultSelection;

  function setSelected(value: Set<string>) {
    setTouched({ sourceId, value });
  }

  function toggle(name: string) {
    const key = tableNameKey(name);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  }

  function handleAdd() {
    onAdd(copyTableEntries(tables ?? [], [...selected], existingTables));
    setSourceId("");
    setTouched(null);
    onOpenChange(false);
  }

  const error = sourcesError ?? tablesError;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add tables from another connector"
      description="Copies each table's fields, primary key and file settings. Client-specific scoping and schedules are not copied — set those here afterwards."
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={selected.size === 0} onClick={handleAdd}>
            {selected.size === 0 ? "Add tables" : `Add ${selected.size} tables`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SourceSelect sources={sources} sourceId={sourceId} onChange={setSourceId} />

        {sources?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No other connector of this type has tables configured yet.
          </p>
        )}

        {sourceId && tables === null && !error && (
          <p className="text-sm text-muted-foreground">Loading tables…</p>
        )}

        {tables !== null && (
          <div className="space-y-2">
            <SelectionToolbar
              selectedCount={selected.size}
              selectableCount={selectable.length}
              onSelectAll={() => setSelected(defaultSelection)}
              onClear={() => setSelected(new Set())}
            />
            <CandidateList candidates={candidates} selected={selected} onToggle={toggle} />
          </div>
        )}

        {error && <FormError assertive>{error}</FormError>}
      </div>
    </FormDialog>
  );
}
